import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';

import { HooksDisplayGrid } from 'ui-components/components/hooks-display-grid';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { ActionablePlaceholder } from 'ui-components/components/display-grid/actionable-placeholder';

import { getProductActivity } from '../../../../../store/products/products.api';
import { useProductDetailsContext } from '../../../use-product-details-context';
import { PAGE_SIZE_500, FIRST_PAGE } from '../../../../../services/pagination-service';
import GridLoading from '../../../../../components/grid-loading';
import UserDateTimezoneFormat from '../../../../../components/user-date-timezone-format';
import { buildColumns, getColumnId } from '../../../../../utils/grid-column-utils';
import { activityGridMessages as gridMessages } from './activity-grid-messages';
import style from './activity-grid.module.less';

const ACTIVITY_PROPERTIES = {
  dateTime: 'dateTime',
  activity: 'activity',
  user: 'user',
};

const COLUMN_CONFIGS = [
  {
    id: ACTIVITY_PROPERTIES.dateTime,
    minWidth: 120,
    width: 180,
    pinned: false,
    nonsortable: true,
    index: 0,
  },
  {
    id: ACTIVITY_PROPERTIES.activity,
    minWidth: 150,
    width: 400,
    pinned: false,
    nonsortable: true,
    index: 1,
  },
  {
    id: ACTIVITY_PROPERTIES.user,
    minWidth: 100,
    width: 180,
    pinned: false,
    nonsortable: true,
    index: 2,
  },
];

const dateTimeName = <FormattedMessage {...gridMessages.columnDateTime} />;
const activityName = <FormattedMessage {...gridMessages.columnActivity} />;
const userName = <FormattedMessage {...gridMessages.columnUser} />;

const BASE_COLUMNS = {
  [ACTIVITY_PROPERTIES.dateTime]: {
    name: dateTimeName,
    render: (data: Record<string, unknown>) => {
      const createDateTime = data.createDateTime as string;
      return createDateTime ? <UserDateTimezoneFormat dateTime={createDateTime} /> : null;
    },
    id: ACTIVITY_PROPERTIES.dateTime,
  },
  [ACTIVITY_PROPERTIES.activity]: {
    name: activityName,
    render: (data: Record<string, unknown>) => data.message as string,
    id: ACTIVITY_PROPERTIES.activity,
  },
  [ACTIVITY_PROPERTIES.user]: {
    name: userName,
    render: (data: Record<string, unknown>) => data.userName as string,
    id: ACTIVITY_PROPERTIES.user,
  },
};

const ActivityGridPlaceholder = () => (
  <ActionablePlaceholder
    description={<FormattedMessage {...gridMessages.noActivityRecordsDescription} />}
    title={<FormattedMessage {...gridMessages.noActivityRecordsTitle} />}
  />
);

export const ProductDetailsActivityV3 = () => {
  const { productId, activity, setActivity } = useProductDetailsContext();
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [gridColumns, setGridColumns] = useState(buildColumns(COLUMN_CONFIGS, BASE_COLUMNS));

  useEffect(() => {
    setIsLoading(true);
    getProductActivity(productId, FIRST_PAGE, PAGE_SIZE_500)
      .then((activityResponse) => {
        setActivity((state) => {
          const result = { ...state };
          for (const act of activityResponse.productActivities as { id: string; [key: string]: unknown }[]) {
            result[act.id] = act;
          }
          return result;
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [productId, setActivity, setIsLoading]);

  const sortedActivities = useMemo(() => {
    const entries = Object.values(activity) as Record<string, unknown>[];
    return [...entries].sort((a, b) => {
      const dateA = DateTime.fromISO(a.createDateTime as string).toMillis();
      const dateB = DateTime.fromISO(b.createDateTime as string).toMillis();
      return dateB - dateA;
    });
  }, [activity]);

  const onColumnsChanged = useCallback((columns: typeof COLUMN_CONFIGS) => {
    setGridColumns(buildColumns(columns, BASE_COLUMNS));
  }, []);

  return (
    <div className={style.container}>
      <HooksDisplayGrid
        // @ts-expect-error Interface not defined
        columns={gridColumns}
        data={!isLoading ? sortedActivities : []}
        disableMultiSelect
        getColumnId={getColumnId}
        onColumnsChanged={onColumnsChanged}
        placeholder={isLoading ? <GridLoading /> : <ActivityGridPlaceholder />}
        renderCheckbox={false}
      />
    </div>
  );
};
